import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SavedVariantTile from '../SavedVariantTile';
import { groupVariantsByGene, rowKeyOf } from './testRecordModel';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// A Test's variants, grouped by gene (TGCLOUD-1866, Phase 2 rework). Each group can take another
// variant in its gene, which fills the variant editor's gene; a gene whose variants are here but
// which genes tested does not list (a Test entered before genes tested existed) says so, and can
// be listed with one click. Nothing is listed on its own: a Test saved before the rework keeps its
// shape until a clinician changes it.

/**
 * @param {object} props
 * @param {object[]} props.variants
 * @param {object[]} props.genesTested
 * @param {boolean} [props.readOnly]
 * @param {Set<string>} [props.fromReport]  findingIds applied from a lab report, not yet saved
 * @param {Set<number>} [props.changed]     indexes in `variants` of the variants that differ from
 *   the Test as saved: an unsaved change (applyProposal.unsavedTestChanges; TGCLOUD-1902)
 * @param {number|null} [props.editingIndex]
 * @param {(index: number) => void} props.onEdit
 * @param {(index: number) => void} props.onDelete
 * @param {(gene: string) => void} props.onAddInGene   `gene` as the group names it
 * @param {(gene: string) => void} props.onListGene    list an unlisted gene under genes tested
 * @param {(variant: object, index: number) => React.ReactNode} [props.renderChecks]  what is
 *   confirmed on a variant's tile while its lab report is checked (TGCLOUD-1868 D3). Drawn here
 *   only, never in the genes tested table's expanded rows, so each group has one Confirm.
 */
export default function VariantsByGene({
  variants,
  genesTested,
  readOnly = false,
  fromReport = new Set(),
  changed = new Set(),
  editingIndex = null,
  onEdit,
  onDelete,
  onAddInGene,
  onListGene,
  renderChecks
}) {
  const t = useAutoTranslation();

  const groups = React.useMemo(() => groupVariantsByGene(variants), [variants]);
  const listed = React.useMemo(
    () => new Set((genesTested || []).map(rowKeyOf)),
    [genesTested]
  );
  if (!groups.length) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('No variants are recorded on this Test.')}
      </Typography>
    );
  }
  return groups.map((group) => (
    <Box
      key={group.key || 'no-gene'}
      component="section"
      aria-label={t('Variants in {{value}}', {
        value: group.label || t('no gene')
      })}
      data-testid={`variant-group-${group.key || 'none'}`}
      sx={{ mb: 1.5 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          mb: 0.5
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {group.label || t('No gene')} ({group.entries.length})
        </Typography>
        {group.key && !listed.has(group.key) && (
          <Typography variant="caption" sx={{ color: 'warning.dark' }}>
            {t('Not listed under genes tested.')}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {!readOnly && group.key && !listed.has(group.key) && (
          <Button size="small" onClick={() => onListGene(group.label)}>
            {t('List under genes tested')}
          </Button>
        )}
        {!readOnly && group.key && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onAddInGene(group.label)}
          >
            {t('Add a variant in {{label}}', { label: group.label })}
          </Button>
        )}
      </Box>
      {group.entries.map(({ variant, index }) => (
        <SavedVariantTile
          key={variant.findingId || index}
          variant={variant}
          fromReport={!!variant.findingId && fromReport.has(variant.findingId)}
          changed={changed.has(index)}
          isEditing={editingIndex === index}
          onEdit={readOnly ? undefined : () => onEdit(index)}
          onDelete={readOnly ? undefined : () => onDelete(index)}
          checks={renderChecks ? renderChecks(variant, index) : null}
        />
      ))}
    </Box>
  ));
}
